import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";

export function PlaceImageCarousel({
  photos,
  alt,
}: {
  photos?: google.maps.places.Photo[];
  alt: string;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const el = containerRef.current;
    if (!el || inView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  if (!photos || photos.length === 0) return null;

  const currentPhotoUrl = inView
    ? photos[currentIndex]?.getURI({ maxWidth: 300 })
    : undefined;
  const showDots = photos.length > 1;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: { xs: "80px", sm: "100px", lg: "150px" },
        height: { xs: "80px", sm: "100px", lg: "150px" },
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {currentPhotoUrl && (
        <Box
          component="img"
          src={currentPhotoUrl}
          alt={alt}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}

      {showDots && !isMobileScreen && (
        <Box
          sx={{
            position: "absolute",
            bottom: 4,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 0.5,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            borderRadius: "12px",
            px: 1,
            py: 0.25,
          }}
        >
          {photos.slice(0, 5).map((_, index) => (
            <IconButton
              key={index}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(index);
              }}
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor:
                  index === currentIndex ? "white" : "rgba(255, 255, 255, 0.5)",
                p: 0,
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
